<script setup lang="ts">
import { computed } from 'vue';
import { get } from 'lodash';

import { isNilOrBlankString, t } from '@nova/core';
import { messages } from '@satellite/helpers/customFieldValidation';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import PhoneNumberFieldV2 from '@satellite/components/elements/forms/PhoneNumberFieldV2.vue';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

import type { CustomFieldProps } from '../types';

/**
 * Phone field type - phone number input with validation
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  input: [value: string];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const defaultPhoneFieldCountry = computed(() => get(props.warehouse, 'country') || 'USA');

/**
 * Ideally, we should be using useCustomFieldValidation, but the phone validation we have there
 * is a generic one and does not provide a complete message as the one we have here.
 *
 */
const rules = computed(() => [
  (value: string, phone: any, messageOptions: any) => {
    /**
     * Blank is decided here in full, for both modes. Vuetify runs every rule against the empty
     * value, so letting a blank optional field fall through to the phone?.valid check below
     * reported "invalid phone number" for a field the user was allowed to leave alone.
     */
    if (isNilOrBlankString(value)) {
      return normalizedProps.required ? messages.required(props.field.label) : true;
    }

    const { example } = messageOptions ?? {};

    if (phone?.valid) {
      return true;
    }

    return example
      ? t('yard.dialogs.yardViewAssetCreateDialog.messages.invalidPhoneNumberWithExample', {
          example
        })
      : t('yard.dialogs.yardViewAssetCreateDialog.messages.invalidPhoneNumber');
  }
]);

const modelValueString = computed(() =>
  typeof props.modelValue === 'string' ? props.modelValue : ''
);

function handleInput(value: string) {
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <phone-number-field-v2
      density="comfortable"
      validate-on="blur invalid-input"
      :country-props="{
        'hide-details': normalizedProps.hideDetails
      }"
      :default-country="defaultPhoneFieldCountry"
      :disabled="normalizedProps.readOnly"
      :input-props="{
        'hide-details': normalizedProps.hideDetails
      }"
      :model-value="modelValueString"
      :rules="rules"
      @update:model-value="handleInput" />
  </custom-field-input-base>
</template>
